import { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  GraduationCap, Briefcase, Users, Building, 
  CheckCircle, Send, FileText, Scale, Heart, Megaphone
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Training images - using provided real photos
const TRAINING_IMG_1 = "/training1.jpg";
const TRAINING_IMG_2 = "/training2.jpg";
const TRAINING_IMG_3 = "/event1.jpg";

const trainingOptions = [
  {
    id: 'schools',
    title: 'Autism Acceptance for Schools',
    description: 'Train educators to create inclusive, supportive classrooms for autistic students.',
    icon: GraduationCap,
    topics: [
      'Understanding autism in the classroom',
      'Sensory-friendly teaching strategies',
      'Communication and behavior support',
      'Creating inclusive learning environments'
    ]
  },
  {
    id: 'workplace',
    title: 'Neurodiversity in the Workplace',
    description: 'Help employers create neuroinclusive workplaces that attract and retain diverse talent.',
    icon: Briefcase,
    topics: [
      'Understanding neurodiversity at work',
      'Reasonable accommodations',
      'Inclusive hiring practices',
      'Supporting autistic employees'
    ]
  },
  {
    id: 'customer_service',
    title: 'Customer Service Training',
    description: 'Train front-line staff to provide excellent service to autistic customers.',
    icon: Users,
    topics: [
      'Recognizing and supporting autistic customers',
      'Communication best practices',
      'Sensory-friendly service delivery',
      'Hidden disabilities awareness'
    ]
  },
  {
    id: 'leadership',
    title: 'Inclusive Leadership',
    description: 'Equip leaders with skills to build and manage neurodiverse teams effectively.',
    icon: Building,
    topics: [
      'Neurodiversity-affirming leadership',
      'Managing diverse teams',
      'Creating psychological safety',
      'Advocacy and allyship'
    ]
  }
];

const advocacyResources = [
  {
    title: 'Rights in Education',
    description: 'Understanding educational rights and how to advocate for your child in school settings. Learn about IEP meetings, accommodations, and working with educators.',
    icon: GraduationCap,
    color: 'bg-blue-100 text-blue-600'
  },
  {
    title: 'Workplace Rights',
    description: 'Know your rights as an autistic employee and how to request reasonable accommodations. Includes information on disclosure and workplace protections.',
    icon: Briefcase,
    color: 'bg-green-100 text-green-600'
  },
  {
    title: 'Healthcare Advocacy',
    description: 'Tips for communicating with healthcare providers and advocating for appropriate care. Includes guidance on finding autism-affirming professionals.',
    icon: Heart,
    color: 'bg-pink-100 text-pink-600'
  },
  {
    title: 'Policy & Legal Guides',
    description: 'Information about autism-related policies in the Cayman Islands and how to engage with policymakers for systemic change.',
    icon: Scale,
    color: 'bg-purple-100 text-purple-600'
  }
];

export default function Training() {
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    training_type: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/training/inquire`, formData);
      toast.success('Inquiry submitted! We will contact you soon.');
      setFormData({ name: '', email: '', organization: '', training_type: '', message: '' });
      setFormOpen(false);
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero with Images */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 section-spacing">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4">Training & Advocacy</Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Building an Inclusive Cayman
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                We offer autism acceptance training for schools, workplaces, and organizations. 
                Let us help you create environments where autistic individuals can thrive.
              </p>
              
              {/* Prominent CTA Button */}
              <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="btn-primary text-lg px-8 py-6" data-testid="request-training-cta">
                    <Megaphone className="mr-2 h-5 w-5" />
                    Request Training
                  </Button>
                </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Request Training</DialogTitle>
                  <DialogDescription>
                    Fill out the form below and we'll get back to you to discuss your training needs.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">First Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="training-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        required
                        data-testid="training-last-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="training-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="organization">Organisation Name *</Label>
                    <Input
                      id="organization"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      required
                      data-testid="training-organization"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="training_type">Training Type</Label>
                    <Select
                      value={formData.training_type}
                      onValueChange={(value) => setFormData({ ...formData, training_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select training type" />
                      </SelectTrigger>
                      <SelectContent>
                        {trainingOptions.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Notes / Additional Information</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your organization and training needs..."
                      rows={4}
                      data-testid="training-message"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting...' : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
            
            {/* Training Images */}
            <div className="grid grid-cols-2 gap-4">
              <img 
                src={TRAINING_IMG_1} 
                alt="Team training session" 
                className="rounded-2xl shadow-lg w-full h-48 object-cover object-top"
              />
              <img 
                src={TRAINING_IMG_2} 
                alt="Collaborative workshop" 
                className="rounded-2xl shadow-lg w-full h-48 object-cover object-top mt-8"
              />
              <img 
                src={TRAINING_IMG_3} 
                alt="Professional presentation" 
                className="rounded-2xl shadow-lg w-full h-48 object-cover col-span-2"
                style={{ objectPosition: '50% 20%' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Advocacy Resources - Moved UP */}
      <section className="py-12 bg-muted/30">
        <div className="container-main">
          <div className="text-center mb-8">
            <Badge className="mb-4">Self-Advocacy</Badge>
            <h2 className="text-2xl font-bold mb-3">Advocacy Resources</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
              Knowledge is power. Learn about your rights and how to advocate effectively for yourself or your loved ones.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {advocacyResources.map((resource, i) => (
              <Card key={i} className="card-base h-full">
                <CardHeader className="pb-2">
                  <div className={`w-10 h-10 rounded-lg ${resource.color} flex items-center justify-center mb-2`}>
                    <resource.icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-base">{resource.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground">{resource.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Programmes */}
      <section className="py-12">
        <div className="container-main">
          <div className="text-center mb-8">
            <Badge className="mb-4">Our Programmes</Badge>
            <h2 className="text-2xl font-bold mb-3">Training Programmes</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
              Comprehensive autism acceptance training tailored to your organisation's needs.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {trainingOptions.map((option) => (
              <Card 
                key={option.id} 
                className="card-base"
                data-testid={`training-${option.id}`}
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <option.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{option.title}</CardTitle>
                      <CardDescription className="mt-2">{option.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <h4 className="font-semibold mb-2 text-sm">Topics Covered:</h4>
                  <ul className="space-y-1">
                    {option.topics.map((topic, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Another CTA at bottom */}
          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-4">Ready to make your organisation more inclusive?</p>
            <Button 
              size="lg" 
              className="btn-primary"
              onClick={() => setFormOpen(true)}
              data-testid="request-training-bottom"
            >
              <Megaphone className="mr-2 h-5 w-5" />
              Request Training Today
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
